import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from './firebase';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import './Home.css';
import logo from './logo.png';
import banner1 from './assets/banners/banner1.png';
import banner2 from './assets/banners/banner2.png';
import banner3 from './assets/banners/banner3.png';
import banner4 from './assets/banners/banner4.png';
import banner5 from './assets/banners/banner5.png';
import downloadImage from './assets/download.png';

// Icon components
const CleaningIcon: React.FC<{ size?: number; color?: string }> = ({ size = 36, color = '#F57C00' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="Cleaning">
    <g fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3h18v18H3z" fill={color} opacity=".15" />
      <path d="M3 3h18v18H3z" />
      <path d="M9 9h6v6H9z" />
      <path d="M15 3v18" />
      <path d="M9 3v18" />
    </g>
  </svg>
);

const LeafIcon: React.FC<{ size?: number; color?: string }> = ({ size = 36, color = '#F57C00' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="Leaf">
    <g fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 3c5.5 2.5 5.5 16.5 0 19" fill={color} opacity=".15" />
      <path d="M6 3c5.5 2.5 5.5 16.5 0 19" />
      <path d="M6 3c-2.5 5.5-2.5 10.5 0 19" />
    </g>
  </svg>
);

const MedkitIcon: React.FC<{ size?: number; color?: string }> = ({ size = 36, color = '#F57C00' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="Medkit">
    <g fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill={color} opacity=".15" />
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14,2 14,8 20,8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10,9 9,9 8,9" />
    </g>
  </svg>
);

const AgricultureIcon: React.FC<{ size?: number; color?: string }> = ({ size = 36, color = '#F57C00' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="Agriculture">
    <path
      d="M12 2c-4 0-8 4-8 8 0 5 8 12 8 12s8-7 8-12c0-4-4-8-8-8zm0 2c3.3 0 6 2.7 6 6 0 3.6-3.8 8.2-6 10.5C9.8 18.2 6 13.6 6 10c0-3.3 2.7-6 6-6z"
      fill={color}
      opacity=".25"
    />
    <path d="M12 4c-2.8 0-5 2.2-5 5 3 0 8-2 8-2s-2 5-2 8c2.8 0 5-2.2 5-5 0-2.8-2.2-5-5-5z" fill="none" stroke={color} strokeWidth="1.8" />
  </svg>
);

const MedicalIcon: React.FC<{ size?: number; color?: string }> = ({ size = 36, color = '#F57C00' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="Medical">
    <rect x="4" y="10" width="16" height="4" rx="1" fill={color} opacity=".2" />
    <path d="M12 5v14M5 12h14" stroke={color} strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const SpeedIcon: React.FC<{ size?: number; color?: string }> = ({ size = 36, color = '#F57C00' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="Speed">
    <g fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="8" />
      <path d="M12 12l4-2" />
      <path d="M7 12h1M9 8l.7.7M15 8l-.7.7M17 12h1" />
    </g>
  </svg>
);

const QualityIcon: React.FC<{ size?: number; color?: string }> = ({ size = 36, color = '#F57C00' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="Quality">
    <g fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l2.6 5.2L20 9l-4 4 .9 5.8L12 16l-4.9 2.8L8 13 4 9l5.4-.8L12 3z" fill={color} opacity=".15" />
      <path d="M12 3l2.6 5.2L20 9l-4 4 .9 5.8L12 16l-4.9 2.8L8 13 4 9l5.4-.8L12 3z" />
      <path d="M9 12l2 2 4-4" />
    </g>
  </svg>
);

const PriceTagIcon: React.FC<{ size?: number; color?: string }> = ({ size = 36, color = '#F57C00' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" role="img" aria-label="Transparent pricing">
    <g fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12l9-9 9 9-9 9-9-9z" fill={color} opacity=".12" />
      <path d="M3 12l9-9 9 9-9 9-9-9z" />
      <circle cx="12" cy="8.5" r="1.4" />
    </g>
  </svg>
);

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [role, setRole] = useState<'provider' | 'customer' | 'guest'>('guest');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [navOpen, setNavOpen] = useState(false);

  const slides = useMemo(
    () => [
      { id: 0, bg: `url(${banner1}) center/cover no-repeat` },
      { id: 1, bg: `url(${banner2}) center/cover no-repeat` },
      { id: 2, bg: `url(${banner3}) center/cover no-repeat` },
      { id: 3, bg: `url(${banner4}) center/cover no-repeat` },
      { id: 4, bg: `url(${banner5}) center/cover no-repeat` },
    ],
    []
  );

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          const data = userDoc.data();
          const r = (data?.role as 'provider' | 'customer') || 'customer';
          setRole(r);
        } catch (e) {
          setRole('customer');
        }
      } else {
        setRole('guest');
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(id);
  }, [slides.length]);

  useEffect(() => {
    document.title = 'Trang chủ - Fconnect';
  }, []);

  return (
    <div className="home-page">
      {/* Header */}
      <header className="home-header">
        <div className="home-container header-inner">
          <div className="brand" onClick={() => { navigate('/'); setNavOpen(false); } }>
            <img src={logo} alt="Fconnect" />
          </div>
          <button
            className="nav-toggle"
            aria-label={navOpen ? 'Đóng menu' : 'Mở menu'}
            aria-expanded={navOpen}
            aria-controls="main-navigation"
            onClick={() => setNavOpen((o) => !o)}
          >
            {navOpen ? '✕' : '☰'}
          </button>
          <nav id="main-navigation" className={`main-nav ${navOpen ? 'open' : ''}`}>
            <Link to="/" onClick={() => setNavOpen(false)}>Trang chủ</Link>
            <Link to="/lao-dong-pho-thong" onClick={() => setNavOpen(false)}>Lao động phổ thông</Link>
            <Link to="/lao-dong-nong-nghiep" onClick={() => setNavOpen(false)}>Lao động nông nghiệp</Link>
            <Link to="/dich-vu-y-te" onClick={() => setNavOpen(false)}>Dịch vụ y tế</Link>
            <Link to="/privacy" onClick={() => setNavOpen(false)}>Chính sách bảo mật</Link>
            <Link to="/terms" onClick={() => setNavOpen(false)}>Điều khoản</Link>
            <Link to="/contact" onClick={() => setNavOpen(false)}>Liên hệ</Link>
            <Link to="/en" className="lang-link" onClick={() => setNavOpen(false)}>English</Link>
          </nav>
        </div>
      </header>
      <div className={`nav-backdrop ${navOpen ? 'open' : ''}`} onClick={() => setNavOpen(false)} />

      {/* Banner / Carousel */}
      <section className="banner-section">
        <div className="carousel">
          {slides.map((slide, idx) => (
            <div
              key={slide.id}
              className={`slide ${idx === currentSlide ? 'active' : ''}`}
              style={{ background: slide.bg }}
            >
            </div>
          ))}
          <div className="carousel-controls">
            <button
              className="carousel-control prev"
              aria-label="Previous slide"
              onClick={() => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)}
            >
              ‹
            </button>
            <button
              className="carousel-control next"
              aria-label="Next slide"
              onClick={() => setCurrentSlide((prev) => (prev + 1) % slides.length)}
            >
              ›
            </button>
          </div>
        </div>
      </section>

      {/* About Fconnect */}
      <section className="about-section">
        <div className="home-container">
          <div className="about-inner">
            <div className="about-copy">
              <h3 className="about-title">Giới thiệu Fconnect</h3>
              <p className="about-subtitle">Fconnect là nền tảng kết nối người dùng với các nhà cung cấp dịch vụ gia đình uy tín, giúp bạn đặt lịch và quản lý mọi thứ chỉ trong vài bước.</p>
              <ul className="about-list">
                <li>Đa dạng ngành nghề: phổ thông, nông nghiệp, y tế...</li>
                <li>Đặt lịch nhanh, theo dõi trạng thái theo thời gian thực</li>
                <li>Đánh giá minh bạch, giá cả rõ ràng</li>
              </ul>
              <div className="about-actions">
                <a
                  className="promo-cta"
                  href="#download"
                  aria-label="Tải app"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('download')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                >
                  Tải app
                </a>
              </div>
            </div>
            <div className="about-visual" aria-hidden="true">
              <div className="about-card">
                <div className="about-badge">Fconnect</div>
                <div className="about-card-title">Kết nối dịch vụ gia đình</div>
                <div className="about-card-sub">Nhanh chóng • Tin cậy • Minh bạch</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="services-section" id="services">
        <div className="home-container">
          <h3 className="section-title">Dịch vụ chính</h3>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon"><CleaningIcon size={56} /></div>
              <h4>Lao động phổ thông</h4>
              <p>Hỗ trợ công việc gia đình, dọn dẹp, phụ việc, lắp đặt nhẹ.</p>
              <Link to="/lao-dong-pho-thong" className="service-cta">Xem chi tiết</Link>
            </div>
            <div className="service-card">
              <div className="service-icon"><LeafIcon size={56} /></div>
              <h4>Lao động nông nghiệp</h4>
              <p>Chăm sóc vườn tược, cây trồng và nông sản với kỹ thuật hiện đại.</p>
              <Link to="/lao-dong-nong-nghiep" className="service-cta">Xem chi tiết</Link>
            </div>
            <div className="service-card">
              <div className="service-icon"><MedkitIcon size={56} /></div>
              <h4>Dịch vụ y tế</h4>
              <p>Xét nghiệm, tư vấn và cung cấp thuốc theo toa với đội ngũ y tế chuyên nghiệp.</p>
              <Link to="/dich-vu-y-te" className="service-cta">Xem chi tiết</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="benefits-section">
        <div className="home-container">
          <h3 className="section-title">Ưu điểm của Fconnect</h3>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon" aria-hidden="true"><SpeedIcon size={56} /></div>
              <h4 className="benefit-title">Nhanh chóng & tiện lợi</h4>
              <p>Đặt lịch chỉ trong vài bước. Theo dõi trạng thái theo thời gian thực, tiết kiệm thời gian cho bạn.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon" aria-hidden="true"><QualityIcon size={56} /></div>
              <h4 className="benefit-title">Chất lượng được đảm bảo</h4>
              <p>Nhà cung cấp được xác minh, đánh giá minh bạch. Tiêu chuẩn dịch vụ luôn được kiểm soát.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon" aria-hidden="true"><PriceTagIcon size={56} /></div>
              <h4 className="benefit-title">Giá minh bạch</h4>
              <p>Giá hiển thị rõ ràng, không phí ẩn. So sánh dễ dàng và chọn gói phù hợp.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Promo Info */}
      <section className="promo-section">
        <div className="home-container">
          <div className="promo-inner">
            <div className="promo-visual" aria-hidden="true">
              <div className="coupon-card">
                <div className="coupon-chip"></div>
                <div className="coupon-content">
                  <div className="coupon-heading">Khuyến mãi hấp dẫn</div>
                  <div className="coupon-value">Ưu đãi đa dạng</div>
                  <div className="coupon-note">Tải app và sử dụng để nhận ưu đãi thường xuyên</div>
                </div>
                <div className="coupon-code">Tải app để nhận ưu đãi</div>
              </div>
            </div>
            <div className="promo-copy">
              <h3 className="promo-title">Thông tin khuyến mãi</h3>
              <p className="promo-subtitle">Nhiều khuyến mãi hấp dẫn khi tải và sử dụng ứng dụng Fconnect: giảm giá, quà tặng, ưu đãi độc quyền và còn nhiều hơn nữa.</p>
              <ul className="promo-list">
                <li>Ưu đãi chào mừng cho người dùng mới</li>
                <li>Khuyến mãi theo mùa và sự kiện đặc biệt</li>
                <li>Quà tặng và điểm thưởng cho thành viên</li>
              </ul>
              <div className="promo-actions">
                <a
                  className="promo-cta"
                  href="#download"
                  aria-label="Tải app"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('download')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                >
                  Tải app
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Download App */}
      <section className="download-section" id="download">
        <div className="home-container">
          <div className="download-inner">
            <div className="download-copy">
              <h3 className="download-title">Trải nghiệm trọn vẹn cùng ứng dụng Fconnect</h3>
              <p className="download-subtitle">Tải ứng dụng để đặt dịch vụ nhanh hơn, theo dõi và quản lý mọi lúc, mọi nơi.</p>
              <div className="store-badges">
                <a
                  className="store-badge appstore-badge"
                  href="https://apps.apple.com/vn/app/fconnect-htp/id6752923786?l=vi"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="badge-icon" aria-hidden="true"></span>
                  <span className="badge-text">
                    <span className="badge-sub">Download on the</span>
                    <span className="badge-brand">App Store</span>
                  </span>
                </a>
                <a
                  className="store-badge play-badge"
                  href="https://play.google.com/store/apps/details?id=com.taichinhhtp.fconnect&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="badge-icon" aria-hidden="true">▶</span>
                  <span className="badge-text">
                    <span className="badge-sub">GET IT ON</span>
                    <span className="badge-brand">Google Play</span>
                  </span>
                </a>
              </div>
            </div>
            <div className="download-visual" aria-hidden="true">
              <img src={downloadImage} alt="Ứng dụng Fconnect" className="download-image" />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="home-footer">
        <div className="home-container footer-inner">
          <div className="footer-contact">
            <p><strong>CÔNG TY CỔ PHẦN TÀI CHÍNH HIỆP TÍN PHÁT</strong></p>
            <p><strong>Email:</strong> trile@taichinh-htp.com</p>
            <p><strong>Điện thoại:</strong> 02513684266</p>
            <p><strong>Địa chỉ:</strong> 11/4C, tổ 15, Kp 6, Phường Trấn Biên, Tỉnh Đồng Nai, Việt Nam</p>
            <p><strong>Thời gian hỗ trợ:</strong> 8:00 - 22:00 (Thứ 2 - Chủ nhật)</p>
          </div>
          <div className="footer-stores">
            <a
              className="store-badge small appstore-badge"
              href="https://apps.apple.com/vn/app/fconnect-htp/id6752923786?l=vi"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="badge-icon" aria-hidden="true"></span>
              <span className="badge-text">
                <span className="badge-sub">Download on the</span>
                <span className="badge-brand">App Store</span>
              </span>
            </a>
            <a
              className="store-badge small play-badge"
              href="https://play.google.com/store/apps/details?id=com.taichinhhtp.fconnect&pcampaignid=web_share"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="badge-icon" aria-hidden="true">▶</span>
              <span className="badge-text">
                <span className="badge-sub">GET IT ON</span>
                <span className="badge-brand">Google Play</span>
              </span>
            </a>
          </div>
        </div>
        <div className="copyright">© 2025 Fconnect. Tất cả các quyền được bảo lưu.</div>
      </footer>
    </div>
  );
};

export default Home;
